import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

const SCALE_VARIANTS = [1, 2, 3, 4, 5, 6];
const ANGLE_VARIANTS = [30, 45, 60];
const pick = (items) => items[Math.floor(Math.random() * items.length)];

function createVariant(angle, scale) {
  if (angle === 45) {
    const diagonal = 6 * scale;
    const height = 3 * scale;
    const baseArea = (diagonal * diagonal) / 2;
    return {
      angle,
      diagonalLatex: `${diagonal}`,
      diagonalLabel: `${diagonal}`,
      halfDiagonalLatex: `${diagonal / 2}`,
      halfDiagonalLabel: `${diagonal / 2}`,
      baseSideLatex: `${3 * scale}\\sqrt{2}`,
      tangentLatex: "1",
      baseArea,
      height,
      volume: (baseArea * height) / 3,
    };
  }

  const diagonalCoefficient = 2 * scale;
  const height = angle === 30 ? scale : 3 * scale;
  const baseArea = 6 * scale * scale;
  return {
    angle,
    diagonalLatex: `${diagonalCoefficient}\\sqrt{3}`,
    diagonalLabel: `${diagonalCoefficient}\u221a3`,
    halfDiagonalLatex: `${scale === 1 ? "" : scale}\\sqrt{3}`,
    halfDiagonalLabel: `${scale === 1 ? "" : scale}\u221a3`,
    baseSideLatex: `${scale === 1 ? "" : scale}\\sqrt{6}`,
    tangentLatex: angle === 30 ? "\\frac{\\sqrt{3}}{3}" : "\\sqrt{3}",
    baseArea,
    height,
    volume: (baseArea * height) / 3,
  };
}

export function generateTask(options = {}) {
  const original = options.variant === "original" && options.session === "2026_MAJ";
  const variant = original
    ? createVariant(30, 4)
    : createVariant(pick(ANGLE_VARIANTS), pick(SCALE_VARIANTS));
  return {
    type: "MaturaSquarePyramidDiagonalAngleVolumeOpenTask",
    data: { ...variant, maxPoints: 2 },
    source: {
      variant: original ? "original" : "random",
      exam: "MATURA",
      session: original ? "2026_MAJ" : undefined,
      taskNumber: "27",
    },
  };
}

function numberValue(value) { const source=String(value?.raw??value??"").replace(",","."); if(source.includes("/")){const [a,b]=source.split("/").map(Number);return a/b;} return Number(source); }
function getDisplayData(data) {
  const angle = data.angle ?? 30;
  const legacyScale = data.n ? data.n / 2 : null;
  const diagonalLatex = data.diagonalLatex ?? `${data.n}\\sqrt{3}`;
  const halfDiagonalLatex = data.halfDiagonalLatex
    ?? (legacyScale ? `${legacyScale === 1 ? "" : legacyScale}\\sqrt{3}` : `\\frac{${diagonalLatex}}{2}`);
  return {
    angle,
    diagonalLatex,
    diagonalLabel: data.diagonalLabel ?? `${data.n}\u221a3`,
    halfDiagonalLatex,
    halfDiagonalLabel: data.halfDiagonalLabel ?? halfDiagonalLatex.replace("\\sqrt{3}", "\u221a3"),
    baseSideLatex: data.baseSideLatex
      ?? (legacyScale ? `${legacyScale === 1 ? "" : legacyScale}\\sqrt{6}` : `\\frac{${diagonalLatex}}{\\sqrt{2}}`),
    tangentLatex: data.tangentLatex ?? (angle === 30 ? "\\frac{\\sqrt{3}}{3}" : angle === 45 ? "1" : "\\sqrt{3}"),
  };
}

function SvgMathLabel({ x, y, width = 54, math, color = "currentColor", align = "center", fontSize = 15, emphasis = false, halo = false }) {
  return <foreignObject x={x} y={y} width={width} height="32" style={{ overflow: "visible" }}>
    <div xmlns="http://www.w3.org/1999/xhtml" style={{ color, fontSize, fontWeight: emphasis ? 700 : 400, lineHeight: "28px", textAlign: align, whiteSpace: "nowrap", textShadow: halo ? "0 0 2px #070629, 0 0 5px #070629, 0 0 8px #070629" : emphasis ? `0 0 5px ${color}` : "none" }}>
      <InlineMath math={math}/>
    </div>
  </foreignObject>;
}

const DIAGRAM_VARIANTS = {
  30: { S: [240, 103], O: [240, 195], A: [70, 220], B: [410, 170], C: [305, 245], D: [180, 145] },
  45: { S: [250, 68], O: [250, 200], A: [100, 225], B: [400, 175], C: [310, 250], D: [190, 145] },
  60: { S: [290, 55], O: [290, 200], A: [180, 225], B: [400, 175], C: [340, 250], D: [235, 145] },
};

const point = ([x, y]) => `${x} ${y}`;
const segment = (from, to) => `M${point(from)} L${point(to)}`;

function unitVector(from, to) {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const length = Math.hypot(dx, dy);
  return [dx / length, dy / length];
}

function angleArcPath(center, fromUnit, toUnit, radius) {
  const startAngle = Math.atan2(fromUnit[1], fromUnit[0]);
  const endAngle = Math.atan2(toUnit[1], toUnit[0]);
  let delta = endAngle - startAngle;
  if (delta > Math.PI) delta -= 2 * Math.PI;
  if (delta < -Math.PI) delta += 2 * Math.PI;
  const samples = Array.from({ length: 13 }, (_, index) => {
    const current = startAngle + delta * (index / 12);
    return [center[0] + Math.cos(current) * radius, center[1] + Math.sin(current) * radius];
  });
  return samples.map((sample, index) => `${index === 0 ? "M" : "L"}${point(sample)}`).join(" ");
}

function PyramidDiagram({ angle, halfDiagonalLatex }) {
  const diagram = DIAGRAM_VARIANTS[angle] ?? DIAGRAM_VARIANTS[30];
  const { S, O, A, B, C, D } = diagram;
  const towardS = unitVector(B, S);
  const towardO = unitVector(B, O);
  const arcRadius = 29;
  const arcPath = angleArcPath(B, towardS, towardO, arcRadius);
  const bisector = [towardS[0] + towardO[0], towardS[1] + towardO[1]];
  const bisectorLength = Math.hypot(...bisector);
  const angleLabel = [
    B[0] + (bisector[0] / bisectorLength) * 45,
    B[1] + (bisector[1] / bisectorLength) * 45,
  ];
  const halfDiagonalLabel = [(O[0] + B[0]) / 2, (O[1] + B[1]) / 2 + 4];
  const heightLabel = [O[0] - 34, (S[1] + O[1]) / 2 - 21];
  return <svg viewBox="0 0 500 300" role="img" aria-label="Ostrosłup prawidłowy czworokątny z zaznaczoną przekątną podstawy i jej połową" style={{width:"min(100%, 500px)",display:"block",margin:"18px auto"}}>
    <path d={`M${point(A)} L${point(C)} L${point(B)} L${point(D)} Z`} fill="none" stroke="currentColor" strokeWidth="2.7"/>
    <path d={`${segment(S, A)} ${segment(S, C)} ${segment(S, B)} ${segment(S, D)}`} fill="none" stroke="currentColor" strokeWidth="2.7"/>
    <path d={segment(A, B)} fill="none" stroke="#8b5cf6" strokeWidth="3.5" strokeDasharray="9 6"/>
    <path d={`${segment(S, O)} ${segment(O, B)}`} fill="none" stroke="#ff9f1a" strokeWidth="3.5"/>
    <path d={arcPath} fill="none" stroke="#ff9f1a" strokeWidth="2.5"/>
    <SvgMathLabel x={halfDiagonalLabel[0] - 36} y={halfDiagonalLabel[1]} width={72} math={halfDiagonalLatex} color="#ff9f1a" halo/>
    <SvgMathLabel x={heightLabel[0]} y={heightLabel[1]} width={34} math="H" color="#ff9f1a" halo/>
    <SvgMathLabel x={angleLabel[0] - 28} y={angleLabel[1] - 14} width={56} math={`${angle}^\\circ`} color="#ff9f1a" fontSize={15} halo/>
    <SvgMathLabel x={S[0] - 25} y={S[1] - 38} width={50} math="S"/>
    <SvgMathLabel x={O[0] - 25} y={O[1] + 1} width={50} math="O"/>
    <SvgMathLabel x={A[0] - 37} y={A[1] - 2} width={30} math="A" align="right"/>
    <SvgMathLabel x={B[0] + 6} y={B[1] - 13} width={30} math="B" align="left"/>
    <SvgMathLabel x={C[0] - 12} y={C[1] + 1} width={38} math="C"/>
    <SvgMathLabel x={D[0] - 29} y={D[1] - 34} width={38} math="D"/>
  </svg>;
}
export function renderTask({data}) { const display = getDisplayData(data); return <div><p>Dany jest ostrosłup prawidłowy czworokątny, którego przekątna podstawy ma długość <InlineMath math={display.diagonalLatex}/>. Krawędź boczna jest nachylona do płaszczyzny podstawy pod kątem <InlineMath math={`${display.angle}^\\circ`}/>.</p><p>Oblicz objętość ostrosłupa.</p></div>; }
export function renderInput(_task,value={},onChange,disabled){const set=(key,next)=>onChange?.({...value,[key]:next});return <div style={{display:"grid",gap:12}}><div><InlineMath math="H="/><FractionInput value={value.height||""} onChange={(next)=>set("height",next)} disabled={disabled} label="H" placeholder="np. 4"/></div><div><InlineMath math="P_p="/><FractionInput value={value.baseArea||""} onChange={(next)=>set("baseArea",next)} disabled={disabled} label="P_p" placeholder="np. 96"/></div><div><InlineMath math="V="/><FractionInput value={value.volume||""} onChange={(next)=>set("volume",next)} disabled={disabled} label="V" placeholder="np. 128"/></div></div>;}
export function checkAnswer({data},value={}){const height=Math.abs(numberValue(value.height)-data.height)<1e-9;const area=Math.abs(numberValue(value.baseArea)-data.baseArea)<1e-9;const method=height||area;const volume=Math.abs(numberValue(value.volume)-data.volume)<1e-9;return{isCorrect:method&&volume,correct:method&&volume,score:Number(method)+Number(volume),maxScore:2,parts:{height,baseArea:area,volume}};}
export function renderAnswer({data}){return <div style={{display:"grid",gap:4}}><InlineMath math={`H=${data.height}`}/><InlineMath math={`P_p=${data.baseArea}`}/><InlineMath math={`V=${data.volume}`}/></div>;}
export function renderUserAnswer(value={}){const height=String(value.height?.raw??value.height??"").trim();const area=String(value.baseArea?.raw??value.baseArea??"").trim();const volume=String(value.volume?.raw??value.volume??"").trim();if(!height&&!area&&!volume)return <i>(brak odpowiedzi)</i>;return <div style={{display:"grid",gap:4}}><InlineMath math={`H=${height?formatLatexPreview(height):"\\text{--}"}`}/><InlineMath math={`P_p=${area?formatLatexPreview(area):"\\text{--}"}`}/><InlineMath math={`V=${volume?formatLatexPreview(volume):"\\text{--}"}`}/></div>;}
export function renderExplanation({data}){const display=getDisplayData(data);return <div><p><strong>Krok 1.</strong> Punkt <InlineMath math="O"/> jest środkiem kwadratowej podstawy, czyli punktem przecięcia jej przekątnych. Dlatego odcinek <InlineMath math="OB"/> jest połową przekątnej <InlineMath math="AB"/>.</p><BlockMath math={`\\lvert AB\\rvert=${display.diagonalLatex},\\qquad \\lvert OB\\rvert=\\frac{\\lvert AB\\rvert}{2}=\\frac{${display.diagonalLatex}}{2}=${display.halfDiagonalLatex}`}/><PyramidDiagram angle={display.angle} halfDiagonalLatex={display.halfDiagonalLatex}/><p><strong>Krok 2.</strong> Wyznaczamy wysokość ostrosłupa z trójkąta prostokątnego <InlineMath math="SOB"/>.</p><BlockMath math={`\\tan ${display.angle}^\\circ=\\frac{\\lvert SO\\rvert}{\\lvert OB\\rvert}=\\frac{H}{${display.halfDiagonalLatex}}`}/><BlockMath math={`H=${display.halfDiagonalLatex}\\cdot\\tan ${display.angle}^\\circ=${display.halfDiagonalLatex}\\cdot${display.tangentLatex}=${data.height}`}/><p><strong>Krok 3.</strong> Obliczamy pole kwadratowej podstawy. Przekątna kwadratu o boku <InlineMath math="a"/> ma długość <InlineMath math={"d=a\\sqrt{2}"}/>, zatem długość boku otrzymujemy, dzieląc przekątną przez <InlineMath math={"\\sqrt{2}"}/>.</p><BlockMath math={`a=\\frac{d}{\\sqrt{2}}=\\frac{${display.diagonalLatex}}{\\sqrt{2}}=${display.baseSideLatex}`}/><p>Następnie korzystamy ze standardowego wzoru na pole kwadratu:</p><BlockMath math={`P_p=a^2=(${display.baseSideLatex})^2=${data.baseArea}`}/><p><strong>Krok 4.</strong> Obliczamy objętość.</p><BlockMath math={`V=\\frac13P_pH=\\frac13\\cdot${data.baseArea}\\cdot${data.height}=${data.volume}`}/></div>;}
